import { useCallback, useEffect, useState } from 'react';
import type { MeetingFolderDTO } from '../../global';

const byName = (a: MeetingFolderDTO, b: MeetingFolderDTO) => a.name.localeCompare(b.name, 'tr');

/**
 * Klasör listesi. Masaüstü köprüsünde klasör yok: `enabled` false kalır, liste boş olur ve
 * kenar menüdeki + eski davranışını korur. Yükleme hatası sessiz: klasörler görünmese de
 * toplantılar çalışmaya devam eder.
 */
export function useFolders(signedIn: boolean) {
  const api = window.zabit.folders;
  const [folders, setFolders] = useState<MeetingFolderDTO[]>([]);

  useEffect(() => {
    if (!signedIn || !api) {
      setFolders([]);
      return;
    }
    let cancelled = false;
    api
      .list()
      .then((list) => {
        if (!cancelled) setFolders(list);
      })
      .catch((err: Error) => console.error('[folders] liste alınamadı:', err.message));
    return () => {
      cancelled = true;
    };
  }, [signedIn, api]);

  const create = useCallback(
    async (name: string) => {
      const folder = await api!.create(name);
      setFolders((prev) => [...prev, folder].sort(byName));
      return folder;
    },
    [api]
  );

  const rename = useCallback(
    async (id: string, name: string) => {
      await api!.rename(id, name);
      setFolders((prev) => prev.map((folder) => (folder.id === id ? { ...folder, name } : folder)).sort(byName));
    },
    [api]
  );

  const remove = useCallback(
    async (id: string) => {
      await api!.remove(id);
      setFolders((prev) => prev.filter((folder) => folder.id !== id));
    },
    [api]
  );

  return { enabled: Boolean(api), folders, create, rename, remove };
}
